import { useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { BrandButton } from "components/brand";
import IconButton from "@mui/material/IconButton";
import { useActionHelper } from "hooks/useActionHelper";

import { changePasswordSchema } from "./ChangePasswordSchema";
import { formErrorHelper, formikValidation } from "helpers/formikValidation";
import { authApi } from "service/authApi";
import { CONSTANTS } from "constants/general";
import { TextField } from "@mui/material";
import { FORM_ITEM_NAMES_OBJ, TEXTFIELD_VARIANTS_OBJ } from "service/constants";
import { Save, Lock, Visibility, VisibilityOff } from "@mui/icons-material";
import { submitForm } from "helpers/formikDataCollect";
import { useDispatch } from "react-redux";

export const ChangePassword = () => {
  const dispatch = useDispatch();
  const [showPasswords, setShowPasswords] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [isLoading, setIsLoading] = useState(false);

  const [changePassword] = useActionHelper({
    createNewItemApiCall: authApi.changePassword,
    customNotify: CONSTANTS.PASSWORD_CHANGE_SUCCESS,
  });

  const [initialValues, validation] = formikValidation(changePasswordSchema);

  const apiCall = changePassword;

  const validationSchema = Yup.object().shape({
    ...validation,
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnChange: true,
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        await submitForm({
          values,
          dispatch,
          apiCall,
        });
      } finally {
        setIsLoading(false);
      }
    },
  });

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  return (
    <div
      className={`change_password_page ${
        isLoading ? "change_password_loading" : ""
      }`}
    >
      <div className="change_password_container">
        <div className="change_password_header">
          <div className="change_password_icon">
            <Lock />
          </div>
          <h1 className="change_password_title">Change Password</h1>
          <p className="change_password_subtitle">
            Update your password to keep your account secure
          </p>
        </div>

        <form onSubmit={formik.handleSubmit} className="change_password_form">
          <div className="password_requirements">
            <h4 className="requirements_title">Password Requirements:</h4>
            <ul className="requirements_list">
              <li>At least 8 characters long</li>
              <li>Contains uppercase and lowercase letters</li>
              <li>Contains at least one number</li>
              <li>Contains at least one special character</li>
            </ul>
          </div>

          <div className="form_field_container">
            <TextField
              autoFocus
              fullWidth
              id={FORM_ITEM_NAMES_OBJ.oldPassword}
              name={FORM_ITEM_NAMES_OBJ.oldPassword}
              label={CONSTANTS.CURRENT_PASSWORD}
              variant={TEXTFIELD_VARIANTS_OBJ.outlined}
              type={showPasswords.oldPassword ? "text" : "password"}
              value={formik.values[FORM_ITEM_NAMES_OBJ.oldPassword]}
              onChange={formik.handleChange}
              error={formErrorHelper({
                formik,
                elementName: FORM_ITEM_NAMES_OBJ.oldPassword,
                isBoolean: true,
              })}
              helperText={formErrorHelper({
                formik,
                elementName: FORM_ITEM_NAMES_OBJ.oldPassword,
              })}
              InputProps={{
                endAdornment: (
                  <IconButton type="button" onClick={() => togglePasswordVisibility("oldPassword")}>
                    {showPasswords.oldPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
          </div>

          <div className="form_field_container">
            <TextField
              fullWidth
              id={FORM_ITEM_NAMES_OBJ.newPassword}
              name={FORM_ITEM_NAMES_OBJ.newPassword}
              label={CONSTANTS.NEW_PASSWORD}
              variant={TEXTFIELD_VARIANTS_OBJ.outlined}
              type={showPasswords.newPassword ? "text" : "password"}
              value={formik.values[FORM_ITEM_NAMES_OBJ.newPassword]}
              onChange={formik.handleChange}
              error={formErrorHelper({
                formik,
                elementName: FORM_ITEM_NAMES_OBJ.newPassword,
                isBoolean: true,
              })}
              helperText={formErrorHelper({
                formik,
                elementName: FORM_ITEM_NAMES_OBJ.newPassword,
              })}
              InputProps={{
                endAdornment: (
                  <IconButton type="button" onClick={() => togglePasswordVisibility("newPassword")}>
                    {showPasswords.newPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
          </div>

          <div className="form_field_container">
            <TextField
              fullWidth
              id={FORM_ITEM_NAMES_OBJ.newPasswordConfirmation}
              name={FORM_ITEM_NAMES_OBJ.newPasswordConfirmation}
              label={CONSTANTS.CONFIRM_PASSWORD}
              variant={TEXTFIELD_VARIANTS_OBJ.outlined}
              type={showPasswords.confirmPassword ? "text" : "password"}
              value={formik.values[FORM_ITEM_NAMES_OBJ.newPasswordConfirmation]}
              onChange={formik.handleChange}
              error={formErrorHelper({
                formik,
                elementName: FORM_ITEM_NAMES_OBJ.newPasswordConfirmation,
                isBoolean: true,
              })}
              helperText={formErrorHelper({
                formik,
                elementName: FORM_ITEM_NAMES_OBJ.newPasswordConfirmation,
              })}
              InputProps={{
                endAdornment: (
                  <IconButton type="button" onClick={() => togglePasswordVisibility("confirmPassword")}>
                    {showPasswords.confirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
          </div>

          <div className="form_actions">
            <BrandButton
              type="submit"
              variant="primary"
              className="save_button"
              disabled={isLoading || !formik.isValid}
            >
              <Save />
              {isLoading ? "Updating..." : "Update Password"}
            </BrandButton>
            <BrandButton
              type="button"
              variant="secondary"
              className="cancel_button"
              onClick={() => formik.resetForm()}
            >
              Cancel
            </BrandButton>
          </div>
        </form>
      </div>
    </div>
  );
};
